/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import { renderStatus } from 'components/v2/cell-renderers/status';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import * as actionsCartoes from '../../../store/cartao/actions';
import { CARTAO_ADMIN } from '../../../constants/modules';
import { hasModuleConfiguration } from '../../../helpers/moduleConfigurationPermission';
import { maskCardNumber } from '../../../utils/maskCardNumber';
import {
  getStatusCartaoLabel,
  getTipoCartaoLabel,
  normalizeCartaoStatus,
  statusCartaoSearchOptions,
  tipoCartaoSearchOptions,
} from './constants';
import Formulario from './Formulario';

const hasPermission = (moduleConfigurations, authData) =>
  hasModuleConfiguration(moduleConfigurations, authData, CARTAO_ADMIN);

const defaultFilter = (moduleConfigurations, authData) => ({
  colaboradorIds:
    !hasPermission(moduleConfigurations, authData) && authData?.id
      ? [authData.id]
      : [],
  colaboradorAtualIds:
    !hasPermission(moduleConfigurations, authData) && authData?.id
      ? [authData.id]
      : [],
});

const buildApiFilter = (localFilter) => ({
  cartaoIds: localFilter.cartaoIds?.length ? localFilter.cartaoIds : null,
  colaboradorIds: localFilter.colaboradorIds?.length ? localFilter.colaboradorIds : null,
  colaboradorAtualIds: localFilter.colaboradorAtualIds?.length ? localFilter.colaboradorAtualIds : null,
  status: localFilter.status?.length ? localFilter.status : null,
  tipo: localFilter.tipo?.length ? localFilter.tipo : null,
});

const getCartaoIdFromSearch = (search) => {
  if (!search) return null;
  const id = new URLSearchParams(search).get('id');
  return id && String(id).trim() ? String(id).trim() : null;
};

const CartoesV2 = ({ authData, exportCartao, history, location, moduleConfigurations }) => {
  const adminPermission = hasPermission(moduleConfigurations, authData);

  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [localFilter, setLocalFilter] = useState(() =>
    defaultFilter(moduleConfigurations, authData));
  const [reload, setReload] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  const defaultFilterMemo = useMemo(
    () => defaultFilter(moduleConfigurations, authData),
    [moduleConfigurations, authData],
  );

  const apiFilter = useMemo(() => {
    const base = buildApiFilter(localFilter);
    if (!adminPermission) {
      return {
        ...base,
        colaboradorIds: authData?.id ? [authData.id] : [],
        colaboradorAtualIds: authData?.id ? [authData.id] : [],
      };
    }
    return base;
  }, [adminPermission, localFilter, authData]);

  useEffect(() => {
    const deepLinkId = getCartaoIdFromSearch(location?.search);
    if (!deepLinkId) return;
    setId(deepLinkId);
    setOpen(true);
  }, [location?.search]);

  const onEdit = (rowId) => {
    setOpen(true);
    setId(rowId);
  };

  const onAdd = () => {
    setOpen(true);
    setId(null);
  };

  const clearDeepLinkId = () => {
    if (!getCartaoIdFromSearch(location?.search)) return;
    const params = new URLSearchParams(location.search);
    params.delete('id');
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  };

  const onClose = (isSave) => {
    setOpen(false);
    setId(null);
    clearDeepLinkId();
    if (isSave) {
      setReload((current) => !current);
    }
  };

  const headerActions = [];
  if (adminPermission) {
    headerActions.push({
      label: 'Novo',
      onClick: onAdd,
      startIcon: <AddIcon />,
      variant: 'contained',
    });
    headerActions.push({
      label: 'Exportar',
      onClick: () => setOpenExport(true),
      startIcon: <FileDownloadOutlinedIcon />,
      variant: 'outlined',
    });
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Cartões</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="cartoes/filtro"
          filterData={apiFilter}
          refreshKey={reload}
          rowHeight={56}
          headerActions={headerActions}
          columns={[
            {
              field: 'codigo',
              headerName: 'Número',
              width: 140,
              renderCell: ({ row }) => maskCardNumber(row.codigo, adminPermission),
            },
            {
              field: 'colaboradorNome',
              headerName: 'Colaborador',
              flex: 1,
              minWidth: 180,
              renderCell: ({ row }) => row.colaboradorNome || row.nome || '—',
            },
            {
              field: 'tipo',
              headerName: 'Tipo',
              width: 130,
              renderCell: ({ row }) => getTipoCartaoLabel(row.tipo),
            },
            {
              field: 'colaboradorAtualNome',
              headerName: 'Colaborador Atual',
              flex: 1,
              minWidth: 180,
              renderCell: ({ row }) => row.colaboradorAtualNome || '—',
            },
            {
              field: 'status',
              headerName: 'Status',
              width: 140,
              renderCell: ({ row }) => {
                const statusValue = normalizeCartaoStatus(row);
                return renderStatus({
                  value: statusValue,
                  label: getStatusCartaoLabel(statusValue, row.ativo),
                });
              },
            },
            {
              field: 'dataCancelamento',
              headerName: 'Data Cancelamento',
              width: 160,
              renderCell: ({ row }) =>
                row.dataCancelamento
                  ? moment(row.dataCancelamento).format('DD/MM/YYYY')
                  : '—',
            },
          ]}
          onEdit={adminPermission ? onEdit : undefined}
          onView={!adminPermission ? onEdit : undefined}
          canDelete={() => false}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilterMemo}
          appliedFilter={localFilter}
          fields={[
            { field: 'useCartao', md: 6, multiSelect: true, key: 'cartaoIds' },
            {
              field: 'useUsuarios',
              key: 'colaboradorIds',
              label: 'Colaborador',
              md: 6,
              multiSelect: true,
              disabled: !adminPermission,
            },
            {
              field: 'useUsuarios',
              key: 'colaboradorAtualIds',
              label: 'Colaborador Atual',
              md: 6,
              multiSelect: true,
              disabled: !adminPermission,
            },
            {
              field: 'useSearchSelect',
              key: 'tipo',
              label: 'Tipo de Cartão',
              options: tipoCartaoSearchOptions,
              md: 6,
              multiSelect: true,
            },
            {
              field: 'useSearchSelect',
              key: 'status',
              label: 'Status',
              options: statusCartaoSearchOptions,
              md: 6,
              multiSelect: true,
            },
          ]}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <Formulario
          id={id}
          open={open}
          onClose={onClose}
          hasPermission={adminPermission}
        />

        <DialogDefaultV2
          open={openExport}
          useFullScreen={false}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => {
            exportCartao(apiFilter, () => {
              setOpenExport(false);
            });
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>Deseja baixar todas as informações filtradas?</DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

CartoesV2.propTypes = {
  authData: PropTypes.object,
  exportCartao: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array,
};

CartoesV2.defaultProps = {
  authData: {},
  moduleConfigurations: [],
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations:
    selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      exportCartao: actionsCartoes.exportCartao,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CartoesV2));
